'use client'

import { useState, useEffect, useCallback } from 'react'
import Modal from '@/components/ui/modal'
import Button from '@/components/ui/button'
import Alert from '@/components/ui/alert'
import {
  deleteEvent,
  joinEvent,
  leaveEvent,
  getEventParticipants,
  getEventAttendanceStats,
  isEventSignupAllowed,
  updateEvent,
} from '@/services/events'
import { EventForm } from './event-form'
import { supabase } from '@/services/supabase'
import type { Game } from '@/services/games'
import { ColorPicker } from '@/components/ui/color-picker'

interface EventDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  event: EventDetails | null
  onEventUpdated?: (updatedEvent: EventDetails) => void
  onEventDeleted?: () => void
}

// Define more specific types for the host and avoid using index signature
interface EventHost {
  id: string
  username: string
  full_name?: string
  avatar_url?: string
}

// Define skill level enum to match exactly what the EventForm expects
type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'all_levels'

// Export the EventDetails interface with improved typing
export interface EventDetails {
  id: string
  title: string
  description?: string
  location?: string
  address?: string
  start: Date | string
  end?: Date | string
  host?: EventHost
  game?: {
    id: string
    name: string
    description?: string | null
    min_players?: number | null
    max_players?: number | null
    min_playtime?: number | null
    max_playtime?: number | null
    image_url?: string | null
    thumbnail_url?: string | null // Add thumbnail_url for BGG games
    bgg_id?: number | null
    complexity?: number | null
    year_published?: number | null
    bgg_rating?: number | null
    weight?: number | null
  }
  minPlayers?: number
  maxPlayers?: number
  skillLevel?: string
  isPrivate?: boolean
  participantCount?: number
  color?: string
  metadata?: {
    game?: {
      name: string
      image_url?: string
      thumbnail_url?: string
      min_players?: number
      max_players?: number
      min_playtime?: number
      max_playtime?: number
      bgg_id?: number
      complexity?: number
      year_published?: number
      bgg_rating?: number
      weight?: number
    }
  }
}

// Type for attendee with profile information
interface Attendee {
  id: string
  created_at: string
  event_id: string
  profile_id: string // This is the direct reference to the user ID
  status: 'im_in' | 'maybe' | 'not_me'
  is_waitlisted: boolean
  waitlist_position: number | null
  rsvp_notes?: string | null
  profiles: {
    // This is the joined profiles data
    id: string
    username: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

// Type for attendance statistics
interface AttendanceStats {
  imIn: number
  maybe: number
  notMe: number
  waitlist: number
  total: number
}

// This needs to match the exact types expected by EventForm
interface EventFormValues {
  title: string
  description: string
  location: string
  address: string
  start_time: string
  end_time: string
  min_players: number
  max_players?: number
  skill_level: SkillLevel // Use the exact union type that EventForm expects
  is_private: boolean
  game_id?: string
  game_data?: string
  color?: string
}

// Define CountObject type at the top with other type definitions
type CountObject = { count: number | string }

// Define a type for the count property that can be either number or string
type CountValue = number | string | { count: number | string } | undefined | null

// Replace any types with proper types
type Profile = {
  id: string
  username: string | null
  full_name: string | null
  avatar_url: string | null
}

// Add these type definitions at the top with other interfaces
type RsvpStatus = 'im_in' | 'maybe' | 'not_me'

// Add type for status object
type StatusObject = {
  count: string | number
  status: RsvpStatus
}

/**
 * Helper function to recursively sanitize objects with count properties
 * to prevent React "Objects are not valid as React child" errors
 */
function sanitizeObjectsForReact<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (typeof obj !== 'object') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObjectsForReact(item)) as unknown as T
  }

  // Handle objects with count property
  if ('count' in obj && Object.keys(obj).length === 1) {
    return (obj as CountObject).count as unknown as T
  }

  // Recursively process all object properties
  const result = { ...obj }
  Object.keys(result).forEach(key => {
    ;(result as Record<string, unknown>)[key] = sanitizeObjectsForReact(
      (result as Record<string, unknown>)[key]
    )
  })

  return result
}

// Safe handler for getting display name
const getSafeDisplayName = (profile: Profile) => {
  if (!profile) return 'Anonymous'

  // Check username for object with count
  if (profile.username && typeof profile.username === 'object' && 'count' in profile.username) {
    return (profile.username as CountObject).count
  }

  // Check full_name for object with count
  if (profile.full_name && typeof profile.full_name === 'object' && 'count' in profile.full_name) {
    return (profile.full_name as CountObject).count
  }

  // Return safe primitive value
  return profile.username || profile.full_name || 'Anonymous'
}

function EventDetailsModal({
  isOpen,
  onClose,
  event,
  onEventUpdated,
  onEventDeleted,
}: EventDetailsModalProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [attendees, setAttendees] = useState<Attendee[]>([])
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats | null>(null)
  const [isRsvpLoading, setIsRsvpLoading] = useState(false)
  const [rsvpError, setRsvpError] = useState<string | null>(null)
  const [userRsvpStatus, setUserRsvpStatus] = useState<'im_in' | 'maybe' | 'not_me' | null>(null)
  const [isUserWaitlisted, setIsUserWaitlisted] = useState(false)
  const [pendingRsvpStatus, setPendingRsvpStatus] = useState<RsvpStatus | null>(null)
  const [isPendingWaitlisted, setIsPendingWaitlisted] = useState(false)
  const [signupAllowed, setSignupAllowed] = useState(true)
  const [signupStartTime, setSignupStartTime] = useState<string | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [rsvpSuccess, setRsvpSuccess] = useState<string | null>(null)
  const [isManagementExpanded, setIsManagementExpanded] = useState(false)
  const [selectedColor, setSelectedColor] = useState<string>('#15803d')
  const [isUpdatingColor, setIsUpdatingColor] = useState(false)
  const [colorUpdateError, setColorUpdateError] = useState<string | null>(null)
  const [colorUpdateSuccess, setColorUpdateSuccess] = useState(false)

  // Helper function to trigger calendar refresh
  const triggerCalendarRefresh = useCallback(() => {
    // Increment refresh counter to trigger the useEffect
    setRefreshCounter(prev => prev + 1)

    // If onEventUpdated callback exists, call it to notify parent components
    if (onEventUpdated && event) {
      onEventUpdated(event)
    }

    // Try to dispatch a refresh event to the calendar
    try {
      // Use a more reliable way to dispatch the event
      if (typeof window !== 'undefined') {
        // Create and dispatch a simple event
        const refreshEvent = document.createEvent('Event')
        refreshEvent.initEvent('calendar:refresh', true, false)
        window.dispatchEvent(refreshEvent)
      }
    } catch (err) {
      console.error('Error dispatching calendar refresh event:', err)
    }
  }, [event, onEventUpdated])

  // Replace verifyDatabasePermissions with useCallback version
  const verifyDatabasePermissions = useCallback(async () => {
    if (!userId) {
      console.warn('Cannot verify database permissions: No user ID')
      return false
    }

    try {
      console.warn('Verifying database permissions for user:', userId)

      // Try to read from the database
      const { data: readData, error: readError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle()

      if (readError) {
        console.error('Database read permission error:', readError)
        return false
      }

      console.warn('Database read test:', readData ? 'Success' : 'Failed')

      // Try a simple write operation to a debug table if exists
      // This is optional and will be skipped if permissions fail
      try {
        const { data: writeData, error: writeError } = await supabase
          .from('debug_logs')
          .insert({
            user_id: userId,
            message: 'Permission test',
            created_at: new Date().toISOString(),
          })
          .select()

        console.warn('Database write test result:', writeData, writeError)

        if (writeError && writeError.code !== '42P01') {
          // 42P01 is "table does not exist"
          console.warn('Database write permission warning:', writeError)
        }
      } catch (writeTestError) {
        // Ignore any errors from the debug table - it might not exist
        console.warn('Debug write test skipped or failed:', writeTestError)
      }

      return true
    } catch (err) {
      console.error('Error verifying database permissions:', err)
      return false
    }
  }, [userId])

  // Update the useEffect to include permission verification
  useEffect(() => {
    async function initialize() {
      const { data } = await supabase.auth.getUser()
      const currentUserId = data.user?.id || null
      setUserId(currentUserId)

      // Verify database permissions
      if (currentUserId) {
        const hasPermissions = await verifyDatabasePermissions()
        if (!hasPermissions && process.env.NODE_ENV === 'development') {
          console.warn('⚠️ User may lack database permissions - RSVP may fail')
        }
      }

      if (event?.id) {
        // Sanitize the entire event object to prevent React child errors
        if (event) {
          // Create a sanitized version of the event
          const sanitizedEvent = sanitizeObjectsForReact(event)

          // If the event was passed by reference, we can update its properties
          Object.entries(sanitizedEvent).forEach(([key, value]) => {
            // @ts-expect-error - Updating event properties
            event[key] = value
          })
        }

        await loadAttendees(event.id, currentUserId)
        await loadAttendanceStats(event.id)
        await checkSignupAllowed(event.id)
      }
    }

    if (isOpen && event) {
      initialize()

      // Reset RSVP states when modal opens
      setPendingRsvpStatus(null)
      setIsPendingWaitlisted(false)
      setRsvpError(null)
    }
  }, [isOpen, event, verifyDatabasePermissions])

  // Add an effect that reruns when the refresh counter changes
  useEffect(() => {
    if (event?.id && userId && refreshCounter > 0) {
      console.warn('Refreshing attendees data due to refresh counter change')
      loadAttendees(event.id, userId)
      loadAttendanceStats(event.id)
    }
  }, [refreshCounter, event?.id, userId])

  // Initialize the selectedColor when the event changes
  useEffect(() => {
    if (event?.color) {
      setSelectedColor(event.color)
    }
  }, [event])

  // Function to update event color
  const handleColorUpdate = async () => {
    if (!event?.id) return

    setIsUpdatingColor(true)
    setColorUpdateError(null)
    setColorUpdateSuccess(false)

    try {
      const { error } = await updateEvent(event.id, { color: selectedColor })

      if (error) {
        throw new Error(error)
      }

      // Update was successful
      setColorUpdateSuccess(true)

      // Clear success message after 2 seconds
      setTimeout(() => {
        setColorUpdateSuccess(false)
      }, 2000)

      // Update the event object to show the change immediately
      if (event && onEventUpdated) {
        const updatedEvent = { ...event, color: selectedColor }
        onEventUpdated(updatedEvent)
      }

      // Trigger calendar refresh to show new color
      triggerCalendarRefresh()
    } catch (err) {
      console.error('Error updating event color:', err)
      setColorUpdateError(err instanceof Error ? err.message : 'Failed to update event color')
    } finally {
      setIsUpdatingColor(false)
    }
  }

  // Improved loadAttendees function with more robust error handling and logging
  const loadAttendees = async (eventId: string, currentUserId: string | null) => {
    console.warn('Loading attendees for event:', eventId, 'current user:', currentUserId)

    if (!eventId) {
      console.error('Cannot load attendees: No event ID provided')
      return
    }

    try {
      // First, let's directly check the current user's RSVP status
      if (currentUserId) {
        console.warn('Checking current user RSVP status...')
        const { data: currentUserData } = await supabase
          .from('event_participants')
          .select('*')
          .eq('event_id', eventId)
          .eq('profile_id', currentUserId)
          .maybeSingle()

        console.warn('Current user RSVP record:', currentUserData)

        if (currentUserData) {
          // Update user-specific state directly from DB
          console.warn('Setting user RSVP status from DB:', currentUserData.status)
          setUserRsvpStatus(currentUserData.status)
          setIsUserWaitlisted(!!currentUserData.is_waitlisted)
        } else {
          console.warn('User has no RSVP record')
          setUserRsvpStatus(null)
          setIsUserWaitlisted(false)
        }
      }

      // Now get all attendees
      console.warn('Fetching all attendees...')
      const { data, error } = await supabase
        .from('event_participants')
        .select(
          `
          *,
          profiles:profile_id (
            id, 
            username,
            full_name,
            avatar_url
          )
        `
        )
        .eq('event_id', eventId)
        .order('created_at', { ascending: true })

      if (error) {
        console.error('Error loading attendees:', error)
        setAttendees([])
        return
      }

      if (!data || data.length === 0) {
        console.warn('No attendees found for event:', eventId)
        setAttendees([])
        return
      }

      console.warn('Attendees loaded successfully:', data.length, 'records')

      // Process the data to match the Attendee type exactly
      const processedAttendees = data.map(item => {
        // Ensure the profile data is complete according to the Attendee interface
        const profileData = item.profiles as Profile // Use Profile type instead of any

        const attendee: Attendee = {
          id: item.id,
          event_id: item.event_id,
          profile_id: item.profile_id,
          status: item.status || 'maybe', // Default to maybe
          is_waitlisted: !!item.is_waitlisted,
          waitlist_position: item.waitlist_position || null,
          created_at: item.created_at,
          rsvp_notes: item.rsvp_notes || null,
          // Ensure the profiles property matches exactly what's expected by the Attendee interface
          profiles: {
            id: profileData?.id || item.profile_id,
            username: profileData?.username || null,
            full_name: profileData?.full_name || 'Unknown User',
            avatar_url: profileData?.avatar_url || null,
          },
        }

        return attendee
      })

      console.warn('Processed attendees:', processedAttendees)

      // Update state with processed attendees that match the Attendee type
      setAttendees(processedAttendees as Attendee[])

      return processedAttendees
    } catch (err) {
      console.error('Exception in loadAttendees:', err)
      return []
    }
  }

  // Load attendance statistics
  const loadAttendanceStats = async (eventId: string) => {
    try {
      console.warn('Loading attendance stats for event:', eventId)

      // Get confirmed count directly from the database - people who are "im_in" and not waitlisted
      const { count: confirmedCount, error: confirmedError } = await supabase
        .from('event_participants')
        .select('*', { count: 'exact', head: false })
        .eq('event_id', eventId)
        .eq('status', 'im_in')
        .eq('is_waitlisted', false)

      console.warn('Confirmed count result:', confirmedCount, confirmedError)

      // Get waitlisted count directly
      const { count: waitlistedCount, error: waitlistedError } = await supabase
        .from('event_participants')
        .select('*', { count: 'exact', head: false })
        .eq('event_id', eventId)
        .eq('is_waitlisted', true)

      console.warn('Waitlisted count result:', waitlistedCount, waitlistedError)

      // Get maybe count
      const { count: maybeCount, error: maybeError } = await supabase
        .from('event_participants')
        .select('*', { count: 'exact', head: false })
        .eq('event_id', eventId)
        .eq('status', 'maybe')

      console.warn('Maybe count result:', maybeCount, maybeError)

      // Get not me count
      const { count: notMeCount, error: notMeError } = await supabase
        .from('event_participants')
        .select('*', { count: 'exact', head: false })
        .eq('event_id', eventId)
        .eq('status', 'not_me')

      console.warn('Not me count result:', notMeCount, notMeError)

      // Calculate total for all participation records
      const { count: totalCount, error: totalError } = await supabase
        .from('event_participants')
        .select('*', { count: 'exact', head: false })
        .eq('event_id', eventId)

      console.warn('Total count result:', totalCount, totalError)

      // Combine the results into a stats object that matches the interface
      const stats: AttendanceStats = {
        imIn: confirmedCount || 0,
        maybe: maybeCount || 0,
        notMe: notMeCount || 0,
        waitlist: waitlistedCount || 0,
        total: totalCount || 0,
      }

      console.warn('Attendance stats:', stats)
      setAttendanceStats(stats)

      return stats
    } catch (err) {
      console.error('Error loading attendance stats:', err)
      setAttendanceStats({
        imIn: 0,
        maybe: 0,
        notMe: 0,
        waitlist: 0,
        total: 0,
      })
      return null
    }
  }

  // Check if signup is allowed for this event
  const checkSignupAllowed = async (eventId: string) => {
    try {
      const { isAllowed, signupStartTime, error } = await isEventSignupAllowed(eventId)

      if (error) {
        console.error('Error checking signup allowed:', error)
        return
      }

      setSignupAllowed(isAllowed !== false)
      setSignupStartTime(signupStartTime || null)
    } catch (err) {
      console.error('Error checking if signup is allowed:', err)
    }
  }

  const isHost = userId && event?.host?.id === userId
  const isAtCapacity =
    attendanceStats?.imIn && event?.maxPlayers ? attendanceStats.imIn >= event.maxPlayers : false

  // Modified to just track the pending selection without committing
  const handleRsvp = (status: 'im_in' | 'maybe' | 'not_me') => {
    setPendingRsvpStatus(status)
    // If status is 'im_in' and the event is at capacity, this would be a waitlist situation
    setIsPendingWaitlisted(status === 'im_in' && isAtCapacity)
  }

  // Add a new function to handle pending removal
  const handlePendingRemoveRsvp = () => {
    setPendingRsvpStatus(null)
    setIsPendingWaitlisted(false)
    setRefreshCounter(prev => prev + 1)
  }

  // Enhanced function to directly check the RSVP change in the database with detailed logging
  const checkRsvpChangeEffect = async () => {
    if (!event?.id || !userId) {
      console.warn('Cannot check RSVP effect: Missing event ID or user ID')
      return null
    }

    try {
      // Directly query Supabase for the current participant record
      const { data, error } = await supabase
        .from('event_participants')
        .select('*')
        .eq('event_id', event.id)
        .eq('profile_id', userId)
        .maybeSingle()

      if (error) {
        console.error('Error checking database state:', error)
        return null
      }

      // Update local state based on the actual database record
      if (data) {
        // Update our local state to match the database
        setUserRsvpStatus(data.status)
        setIsUserWaitlisted(!!data.is_waitlisted)

        return data
      } else {
        setUserRsvpStatus(null)
        setIsUserWaitlisted(false)
        return null
      }
    } catch (err) {
      console.error('Exception checking RSVP state:', err)
      return null
    }
  }

  // Modified commitRsvp function to match database schema requirements
  const commitRsvp = async () => {
    if (!event?.id || !userId) {
      console.warn('Missing event ID or user ID, cannot commit RSVP')
      alert('Missing event ID or user ID - please refresh and try again')
      return
    }

    setIsRsvpLoading(true)
    setRsvpError(null)

    try {
      // Check initial state before changes
      await checkRsvpChangeEffect()

      let dbOperationSuccess = false

      // Check if this is a removal action
      if (pendingRsvpStatus === ('remove' as RsvpStatus)) {
        // Try direct database deletion to ensure it works
        const { error: directDeleteError } = await supabase
          .from('event_participants')
          .delete()
          .eq('event_id', event.id)
          .eq('profile_id', userId)

        if (directDeleteError) {
          console.error('Direct DB delete error:', directDeleteError)
          throw new Error(`Database delete failed: ${directDeleteError.message}`)
        } else {
          dbOperationSuccess = true
        }

        // Reset user's RSVP status immediately
        setUserRsvpStatus(null)
        setIsUserWaitlisted(false)
      } else if (pendingRsvpStatus) {
        // Regular RSVP action
        // Check if record exists first
        const { data: existingRecord } = await supabase
          .from('event_participants')
          .select('id')
          .eq('event_id', event.id)
          .eq('profile_id', userId)
          .maybeSingle()

        let directResult
        if (existingRecord) {
          // Update existing record directly
          const { data, error } = await supabase
            .from('event_participants')
            .update({
              status: pendingRsvpStatus,
              is_waitlisted: isPendingWaitlisted,
            })
            .eq('id', existingRecord.id)
            .select()

          directResult = { data, error }
        } else {
          // Insert new record directly
          const { data, error } = await supabase
            .from('event_participants')
            .insert({
              event_id: event.id,
              profile_id: userId,
              status: pendingRsvpStatus,
              is_waitlisted: isPendingWaitlisted,
            })
            .select()

          directResult = { data, error }
        }

        if (directResult.error) {
          console.error('Database operation failed:', directResult.error)
          throw new Error(`Database operation failed: ${directResult.error.message}`)
        } else if (!directResult.data || directResult.data.length === 0) {
          console.error('Database operation returned no data')
          throw new Error('Database operation completed but returned no data')
        } else {
          dbOperationSuccess = true

          // Update user's RSVP status based on the response
          setUserRsvpStatus(pendingRsvpStatus)
          setIsUserWaitlisted(isPendingWaitlisted)
        }
      }

      if (dbOperationSuccess) {
        // Pause briefly to ensure DB changes are committed
        await new Promise(resolve => setTimeout(resolve, 500))

        // Check if the change took effect
        await checkRsvpChangeEffect()

        // Now refresh all UI data
        await loadAttendees(event.id, userId)
        await loadAttendanceStats(event.id)

        // Clear the pending status after committing
        setPendingRsvpStatus(null)
        setIsPendingWaitlisted(false)

        // Trigger calendar refresh
        triggerCalendarRefresh()

        // Set success message based on operation
        if (pendingRsvpStatus === ('remove' as RsvpStatus)) {
          setRsvpSuccess('Your RSVP has been successfully removed.')
        } else {
          setRsvpSuccess(
            `You have successfully RSVP'd as "${
              pendingRsvpStatus === 'im_in'
                ? "I'm in"
                : pendingRsvpStatus === 'maybe'
                ? 'Maybe'
                : 'Not me'
            }".`
          )
        }

        // Clear success message after a short delay and close the modal
        setTimeout(() => {
          setRsvpSuccess(null)
          safelyCloseModal()

          // Trigger another refresh after modal closes
          triggerCalendarRefresh()
        }, 1500)
      } else {
        console.error('DB operation did not complete successfully')
        throw new Error('RSVP action did not complete successfully')
      }
    } catch (err) {
      console.error('Error RSVPing to event:', err)
      setRsvpError(err instanceof Error ? err.message : 'Failed to RSVP to event')

      // Show alert for debugging purposes
      if (process.env.NODE_ENV === 'development') {
        alert(`RSVP Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      }
    } finally {
      setIsRsvpLoading(false)
    }
  }

  // Function to cancel/reset pending RSVP
  const cancelRsvp = () => {
    console.warn('Canceling pending RSVP')
    setPendingRsvpStatus(null)
    setIsPendingWaitlisted(false)
    setRsvpError(null)
  }

  // Handle deleting the event
  const handleDelete = async () => {
    if (!event?.id) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      const result = await deleteEvent(event.id)

      if ('error' in result && result.error) {
        throw new Error(result.error)
      }

      // Notify parent component that event was deleted
      if (onEventDeleted) {
        onEventDeleted()
      }

      safelyCloseModal()
    } catch (err) {
      console.error('Error deleting event:', err)
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete event')
    } finally {
      setIsDeleting(false)
    }
  }

  // Format date and time
  const formatDateTime = (dateStr: string | Date) => {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
    return date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Convert string skill level to the expected union type
  const mapSkillLevel = (level?: string): SkillLevel => {
    switch (level) {
      case 'beginner':
        return 'beginner'
      case 'intermediate':
        return 'intermediate'
      case 'advanced':
        return 'advanced'
      default:
        return 'all_levels'
    }
  }

  // Update the renderSafeStringValue function
  const renderSafeStringValue = (value: CountValue): string => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    if (typeof value === 'object' && value !== null && 'count' in value) {
      return String((value as CountObject).count)
    }
    return String(value)
  }

  // Update the getNumericValue function
  const getNumericValue = (value: CountValue): number => {
    if (value === null || value === undefined) return 0
    if (typeof value === 'number') return value
    if (typeof value === 'string' && !isNaN(Number(value))) return Number(value)
    if (typeof value === 'object' && value !== null && 'count' in value) {
      const count = (value as CountObject).count
      if (typeof count === 'number') return count
      if (typeof count === 'string' && !isNaN(Number(count))) return Number(count)
    }
    return 0
  }

  // If in editing mode, show the event form
  if (isEditing && event) {
    // Check for game data either in game field or metadata
    const gameData = event.game || event.metadata?.game

    // Create initialValues object from event data
    const initialValues: Partial<EventFormValues> = {
      title: event.title,
      description: event.description || '',
      location: event.location || '',
      address: event.address || '',
      start_time: new Date(event.start).toISOString(),
      end_time: event.end ? new Date(event.end).toISOString() : '',
      min_players: event.minPlayers || 1,
      max_players: event.maxPlayers,
      skill_level: mapSkillLevel(event.skillLevel),
      is_private: event.isPrivate || false,
      game_id: gameData && 'id' in gameData ? gameData.id : undefined,
      color: event.color || '#15803d', // Add event color with default
    }

    return (
      <Modal
        isOpen={isOpen}
        title="Edit Event"
        onClose={() => {
          if (hasUnsavedChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
              setIsEditing(false)
            }
          } else {
            setIsEditing(false)
          }
        }}
      >
        <EventForm
          eventId={event.id}
          initialGame={event.game as Game}
          initialValues={initialValues}
          initialDate={new Date(event.start)}
          initialEndDate={event.end ? new Date(event.end) : undefined}
          onSuccess={() => {
            setHasUnsavedChanges(false)
            setIsEditing(false)
            if (onEventUpdated && event) onEventUpdated(event)
          }}
          onCancel={() => {
            setHasUnsavedChanges(false)
            setIsEditing(false)
          }}
          isEdit={true}
          useDatePicker={true}
        />
      </Modal>
    )
  }

  // Get status badge color
  const getStatusBadgeColor = (status: RsvpStatus) => {
    switch (status) {
      case 'im_in':
        return 'bg-green-600 text-white'
      case 'maybe':
        return 'bg-yellow-500 text-black'
      case 'not_me':
        return 'bg-red-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  // Update the renderStatusBadge function to handle StatusObject
  const renderStatusBadge = (status: RsvpStatus | StatusObject) => {
    const text = {
      im_in: "I'm in!",
      maybe: 'Maybe',
      not_me: 'Not me',
    }

    const actualStatus = typeof status === 'object' ? status.status : status

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(actualStatus)}`}>
        {text[actualStatus]}
      </span>
    )
  }

  // Ensure we have a safe way to close the modal
  const safelyCloseModal = () => {
    // Clear all pending states
    setPendingRsvpStatus(null)
    setIsPendingWaitlisted(false)
    setIsRsvpLoading(false)
    setRsvpError(null)
    setIsEditing(false)

    // Wait a tick to let React process state updates before closing
    setTimeout(() => {
      onClose()
    }, 0)
  }

  // Update type for attendee count
  const renderAttendeeCount = (count: CountValue): string => {
    if (typeof count === 'object' && count !== null && 'count' in count) {
      return `Attendees (${count.count})`
    }
    return `Attendees (${count ?? 0})`
  }

  return (
    <Modal isOpen={isOpen} title={event?.title || 'Event Details'} onClose={safelyCloseModal}>
      {event ? (
        <div className="space-y-4">
          {/* Main content */}
          <div className="space-y-3">
            {/* Remove duplicate title and move private label next to modal title */}
            {event.isPrivate && (
              <div className="flex justify-end -mt-2 mb-2">
                <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded-full">
                  Private
                </span>
              </div>
            )}

            {/* Process event data to ensure we're not rendering objects */}
            {(() => {
              // Extract and process any potential object values from event
              const safeEvent = { ...event }

              // Handle participantCount which might be an object with {count} property
              if (
                typeof safeEvent.participantCount === 'object' &&
                safeEvent.participantCount !== null &&
                'count' in (safeEvent.participantCount as CountObject)
              ) {
                const count = (safeEvent.participantCount as CountObject).count
                safeEvent.participantCount = typeof count === 'number' ? count : undefined
              }

              return null // Just processing data, not rendering anything
            })()}

            {/* Game information - BGG style */}
            {(event.game || event.metadata?.game) && (
              <div className="bg-gray-800 rounded-md overflow-hidden mb-4">
                {/* Make the entire card clickable if BGG ID exists */}
                <div
                  className={`${
                    event.game?.bgg_id
                      ? 'cursor-pointer hover:bg-gray-700/50 transition-colors'
                      : ''
                  }`}
                  onClick={() => {
                    if (event.game?.bgg_id) {
                      window.open(
                        `https://boardgamegeek.com/boardgame/${event.game.bgg_id}`,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  }}
                >
                  {/* Game header with name and thumbnail */}
                  <div className="bg-gray-700 p-3 border-b border-gray-600 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Thumbnail */}
                      {(event.game?.thumbnail_url || event.metadata?.game?.thumbnail_url) && (
                        <img
                          src={event.game?.thumbnail_url || event.metadata?.game?.thumbnail_url}
                          alt={event.game?.name || event.metadata?.game?.name}
                          className="w-14 h-14 object-cover rounded"
                        />
                      )}
                      {/* Name and year */}
                      <div>
                        <h3 className="text-xl font-semibold text-white">
                          {event.game?.name || event.metadata?.game?.name}
                          {(event.game?.year_published || event.metadata?.game?.year_published) && (
                            <span className="text-gray-400 ml-2">
                              ({event.game?.year_published || event.metadata?.game?.year_published})
                            </span>
                          )}
                        </h3>
                      </div>
                    </div>
                    {/* External link icon */}
                    {event.game?.bgg_id && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Game details with BGG-like layout */}
                  <div className="p-3 grid grid-cols-2 gap-3">
                    {/* Left column - Players and BGG Rating */}
                    <div className="space-y-3 border-r border-gray-600 pr-3">
                      <div>
                        <div className="text-gray-400 text-sm">Players</div>
                        <div className="text-white text-lg">
                          {event.game?.min_players || event.metadata?.game?.min_players || '?'}-
                          {event.game?.max_players || event.metadata?.game?.max_players || '?'}
                        </div>
                      </div>
                      {/* Show BGG Rating if available from any source */}
                      {(event.game?.bgg_rating || event.metadata?.game?.bgg_rating) && (
                        <div>
                          <div className="text-gray-400 text-sm">BGG Rating</div>
                          <div className="text-white text-lg flex items-center">
                            <span className="text-yellow-400 mr-1">★</span>
                            {(event.game?.bgg_rating || event.metadata?.game?.bgg_rating)?.toFixed(
                              1
                            ) || 'N/A'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right column - Playtime and Weight */}
                    <div className="space-y-3 pl-1">
                      <div>
                        <div className="text-gray-400 text-sm">Playtime</div>
                        <div className="text-white text-lg">
                          {(() => {
                            const minPlaytime =
                              event.game?.min_playtime || event.metadata?.game?.min_playtime
                            const maxPlaytime =
                              event.game?.max_playtime || event.metadata?.game?.max_playtime
                            if (minPlaytime === maxPlaytime) {
                              return `${minPlaytime} min`
                            }
                            return `${minPlaytime || '?'}-${maxPlaytime || '?'} min`
                          })()}
                        </div>
                      </div>
                      {/* Show Weight if available from any source */}
                      {(event.game?.weight ||
                        event.game?.complexity ||
                        event.metadata?.game?.complexity ||
                        event.metadata?.game?.weight) && (
                        <div>
                          <div className="text-gray-400 text-sm">Weight</div>
                          <div className="text-white text-lg">
                            <span className="text-red-400">
                              {(
                                event.game?.weight ||
                                event.game?.complexity ||
                                event.metadata?.game?.weight ||
                                event.metadata?.game?.complexity
                              )?.toFixed(1)}
                              /5
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Date and time */}
            <div className="flex items-start space-x-3">
              <div className="text-secondary mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-white">{formatDateTime(event.start)}</p>
                {event.end && <p className="text-gray-400">to {formatDateTime(event.end)}</p>}
              </div>
            </div>

            {/* Location */}
            {event.location && (
              <div className="flex items-start space-x-3">
                <div className="text-secondary mt-0.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-white">{event.location}</p>
                  {event.address && <p className="text-gray-400">{event.address}</p>}
                </div>
              </div>
            )}

            {/* Host */}
            {event.host && (
              <div className="flex items-start space-x-3">
                <div className="text-secondary mt-0.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-white">Host: {event.host.full_name || event.host.username}</p>
                </div>
              </div>
            )}

            {/* Player count */}
            {(event.minPlayers || event.maxPlayers) && (
              <div className="flex items-start space-x-3">
                <div className="text-secondary mt-0.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                </div>
                <div>
                  <div>
                    <table className="w-full">
                      <tbody>
                        <tr>
                          <td className="text-white">
                            Players: {renderSafeStringValue(event.minPlayers) || 1}
                            {event.maxPlayers && ` - ${renderSafeStringValue(event.maxPlayers)}`}
                          </td>
                          <td className="text-right">
                            {attendanceStats && event.maxPlayers && (
                              <span
                                className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                                  getNumericValue(attendanceStats.imIn) >=
                                  getNumericValue(event.maxPlayers)
                                    ? 'bg-red-900 text-red-200'
                                    : getNumericValue(attendanceStats.imIn) >=
                                      getNumericValue(event.maxPlayers) * 0.8
                                    ? 'bg-yellow-700 text-yellow-100'
                                    : 'bg-green-800 text-green-100'
                                }`}
                              >
                                {Math.max(
                                  0,
                                  getNumericValue(event.maxPlayers) -
                                    getNumericValue(attendanceStats.imIn)
                                )}
                                /{renderSafeStringValue(event.maxPlayers)} slots available
                              </span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {attendanceStats && (
                      <p className="text-gray-400 mt-1">
                        Currently:{' '}
                        {typeof attendanceStats.imIn === 'object' &&
                        attendanceStats.imIn !== null &&
                        'count' in attendanceStats.imIn
                          ? (attendanceStats.imIn as any).count
                          : attendanceStats.imIn}{' '}
                        confirmed
                        {attendanceStats.waitlist !== undefined &&
                          (typeof attendanceStats.waitlist === 'object' &&
                          attendanceStats.waitlist !== null &&
                          'count' in attendanceStats.waitlist
                            ? `, ${(attendanceStats.waitlist as any).count} waitlisted`
                            : attendanceStats.waitlist > 0
                            ? `, ${attendanceStats.waitlist} waitlisted`
                            : '')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Skill level */}
            {event.skillLevel && (
              <div className="flex items-start space-x-3">
                <div className="text-secondary mt-0.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-white">
                    Skill level:{' '}
                    {event.skillLevel === 'all_levels'
                      ? 'All levels'
                      : event.skillLevel.charAt(0).toUpperCase() + event.skillLevel.slice(1)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* RSVP buttons - show for logged in users including hosts */}
          {userId && (
            <div className="mt-6">
              <h4 className="text-md font-semibold text-white mb-3">RSVP</h4>

              {rsvpError && (
                <Alert variant="error" className="mb-3">
                  {rsvpError}
                </Alert>
              )}

              {rsvpSuccess && (
                <Alert variant="success" className="mb-3">
                  {rsvpSuccess}
                </Alert>
              )}

              {/* Add explanation text for the RSVP flow - commented out but kept for reference */}
              {/*
              {!userRsvpStatus && !pendingRsvpStatus && (
                <div className="mb-3 py-2 px-3 bg-gray-800 rounded-md">
                  <p className="text-sm text-gray-300">
                    <span className="text-blue-400">ℹ️</span> Select your RSVP status below. After
                    selection, click "OK" to confirm.
                  </p>
                </div>
              )}
              */}

              <div className="flex flex-wrap gap-2">
                {/* If user already has a status, show their current status */}
                {userRsvpStatus && (
                  <div className="w-full mb-2 py-2 px-3 bg-gray-800 rounded-md">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-white">Your response: </span>
                        {/* Only show status badge if not waitlisted, otherwise show just the waitlisted badge */}
                        {!isUserWaitlisted && renderStatusBadge(userRsvpStatus)}
                        {isUserWaitlisted && (
                          <span className="ml-2 px-2 py-1 text-xs bg-purple-600 text-white rounded-full">
                            Waitlisted
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handlePendingRemoveRsvp}
                        disabled={isRsvpLoading}
                        className="text-gray-400 hover:text-red-400"
                        aria-label="Remove RSVP"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </Button>
                    </div>
                  </div>
                )}

                {/* If there's a pending RSVP selection, show it */}
                {pendingRsvpStatus && (
                  <div className="w-full mb-2 py-2 px-3 bg-gray-700 rounded-md border border-primary">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-white">Your selection: </span>
                        {pendingRsvpStatus === ('remove' as RsvpStatus) ? (
                          <span className="ml-2 px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                            Remove RSVP
                          </span>
                        ) : (
                          <>
                            {pendingRsvpStatus &&
                              !isPendingWaitlisted &&
                              renderStatusBadge(pendingRsvpStatus)}
                            {isPendingWaitlisted && (
                              <span className="ml-2 px-2 py-1 text-xs bg-purple-600 text-white rounded-full">
                                Waitlisted
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <span className="text-gray-400 text-sm italic">Click OK to confirm</span>
                    </div>
                  </div>
                )}

                {/* Always show RSVP buttons */}
                <div className={`w-full ${pendingRsvpStatus ? 'mt-2' : ''}`}>
                  {/* I'm in button - disabled when at capacity and not already waitlisted */}
                  <Button
                    onClick={() => handleRsvp('im_in')}
                    className={`bg-green-600 hover:bg-green-700 ${
                      userRsvpStatus === 'im_in' && !isUserWaitlisted ? 'ring-2 ring-white' : ''
                    }`}
                    disabled={
                      isRsvpLoading || (isAtCapacity && !isUserWaitlisted) || !signupAllowed
                    }
                  >
                    I'm in!
                  </Button>

                  {/* Waitlist button - only show when event is at capacity */}
                  {isAtCapacity && (
                    <Button
                      onClick={() => handleRsvp('im_in')}
                      className={`bg-purple-600 hover:bg-purple-700 ml-2 ${
                        isUserWaitlisted ? 'ring-2 ring-white' : ''
                      }`}
                      disabled={isRsvpLoading || !signupAllowed}
                    >
                      Join Waitlist
                    </Button>
                  )}

                  {/* Always show Maybe and Not me buttons */}
                  <Button
                    onClick={() => handleRsvp('maybe')}
                    className={`bg-yellow-500 hover:bg-yellow-600 text-black ml-2 ${
                      userRsvpStatus === 'maybe' ? 'ring-2 ring-white' : ''
                    }`}
                    disabled={isRsvpLoading || !signupAllowed}
                  >
                    Maybe
                  </Button>

                  <Button
                    onClick={() => handleRsvp('not_me')}
                    className={`bg-red-500 hover:bg-red-600 ml-2 ${
                      userRsvpStatus === 'not_me' ? 'ring-2 ring-white' : ''
                    }`}
                    disabled={isRsvpLoading || !signupAllowed}
                  >
                    Not me
                  </Button>

                  {/* Add a message when signup is not allowed */}
                  {!signupAllowed && signupStartTime && (
                    <div className="w-full mt-2 py-2 px-3 bg-gray-700 rounded-md">
                      <p className="text-sm text-yellow-300">
                        RSVPs for this event will open at{' '}
                        {new Date(signupStartTime).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Display attendees */}
          {attendees && attendees.length > 0 && (
            <div className="mt-4">
              <h3 className="text-lg font-medium">{renderAttendeeCount(event.participantCount)}</h3>
              <ul className="mt-2 space-y-1">
                {attendees.map((attendee, idx) => {
                  // Use our safe display name function instead of inline checks
                  const displayName = getSafeDisplayName(attendee.profiles)

                  // Safely check status properties
                  const status =
                    typeof attendee.status === 'object' &&
                    attendee.status !== null &&
                    'count' in attendee.status
                      ? (attendee.status as any).count
                      : attendee.status

                  // Safe emoji selection
                  let statusEmoji = '✅ '
                  if (status === 'im_in') statusEmoji = '👑 '
                  else if (status === 'maybe') statusEmoji = '⌛ '

                  return (
                    <li key={idx} className="flex items-center text-sm">
                      <span className="mr-2">{statusEmoji}</span>
                      {displayName}
                      {attendee.profiles.id === userId && ' (You)'}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-6 flex flex-wrap gap-2">
            {/* Add OK and Cancel buttons at the bottom - only when there's a pending action */}
            {pendingRsvpStatus ? (
              <div className="w-full flex justify-end space-x-2 mt-4">
                <Button variant="secondary" onClick={cancelRsvp} disabled={isRsvpLoading}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={commitRsvp} disabled={isRsvpLoading}>
                  {isRsvpLoading ? 'Processing...' : 'OK'}
                </Button>
                {isRsvpLoading && (
                  <div className="w-full mt-2 text-center">
                    <p className="text-sm text-primary animate-pulse">Processing your RSVP...</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full flex justify-end mt-4">
                <Button variant="secondary" onClick={safelyCloseModal}>
                  Close
                </Button>
              </div>
            )}
          </div>

          {/* Delete error message */}
          {deleteError && (
            <Alert variant="error" className="mt-4">
              {deleteError}
            </Alert>
          )}

          {/* Convert event management section to a collapsible dropdown */}
          {isHost && (
            <div className="mt-8 pt-6 border-t border-gray-700">
              <button
                onClick={() => setIsManagementExpanded(!isManagementExpanded)}
                className="w-full flex items-center justify-between text-lg font-medium text-gray-300 mb-4 hover:text-white focus:outline-none"
              >
                <span>Event Management</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 transition-transform ${
                    isManagementExpanded ? 'transform rotate-180' : ''
                  }`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {isManagementExpanded && (
                <div className="space-y-4 animate-in fade-in-0 duration-200">
                  {/* Color picker section */}
                  <div className="bg-gray-800 p-3 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-white">Event Color</label>
                      {colorUpdateSuccess && (
                        <span className="text-xs text-green-400">Color updated!</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <ColorPicker
                        value={selectedColor}
                        onChange={setSelectedColor}
                        title="Choose event color"
                        buttonClassName="bg-gray-700"
                      />
                      <Button
                        onClick={handleColorUpdate}
                        disabled={isUpdatingColor}
                        variant="secondary"
                        className="text-sm"
                        style={{ backgroundColor: selectedColor }}
                      >
                        {isUpdatingColor ? 'Updating...' : 'Apply Color'}
                      </Button>
                    </div>
                    {colorUpdateError && (
                      <p className="mt-2 text-sm text-red-400">{colorUpdateError}</p>
                    )}
                  </div>

                  {/* Existing buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => setIsEditing(true)}
                      className="flex-1"
                      disabled={isDeleting}
                    >
                      <span className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        Edit Event
                      </span>
                    </Button>
                    <Button
                      onClick={handleDelete}
                      variant="destructive"
                      className="flex-1"
                      disabled={isDeleting}
                    >
                      <span className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {isDeleting ? 'Deleting...' : 'Delete Event'}
                      </span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="py-4 text-center text-gray-400">Loading event details...</div>
      )}
    </Modal>
  )
}

export { EventDetailsModal }
